import React from "react";
import {
    Redirect,
    Route,
    RouteComponentProps,
    Switch,
    useHistory
} from "react-router-dom";
import { CourseContext, CourseListAtom } from "./Store";
import CourseLayout from "./components/CourseLayout";
import SignIn from "./SignIn";
import RollCall from "./RollCall";
import Vote from "./Vote";
import Homework from "./Homework";
import useRequest from "Utils/useRequest";
import CourseSpin from "./components/CourseSpin";
import { isTrue } from "../../Utils/filter";
import { LayoutRoute } from "components/LayoutRoute";
import SignInDetail from "./SignInDetail";
import VoteDetail from "./VoteDetail";
import Experiment from "./Experiment";
import Exercise from "./Exercise";
import ExerciseTopics from "./ExerciseTopics";
import ExerciseDetail from "./ExerciseDetail";
import Courseware from "./Courseware";
import Teams from "./Teams";
import TeamLayout from "./components/TeamLayout";
import MyTeam from "./Teams/Page/MyTeam";
import TeamDetail from "./Teams/Page/TeamDetail";
import { useAtomValue } from "jotai";
import { getCourseDetail } from "../../API";
import Report from "./Report";
import Meeting from "./Meeting/List";
import MeetingDetail from "./Meeting/Detail";
import Question from "./Question";
import QuestionDetail from "./Question/QuestionDetail";
import QuestionTopics from "./Question/QuestionTopics";
import Discuss from "./Discuss/List";
import { DiscussDetail } from "./Discuss/DiscussDetail";
import { SubLayout } from "../../components/Layouts/SubLayout";

interface CourseDetailProps {
    weekTimeId: string;
    courseNo: string;
    courseSeq: string;
}

export default function CourseDetail({
    match
}: RouteComponentProps<CourseDetailProps>) {
    const history = useHistory();
    const { weekTimeId, courseNo, courseSeq } = match.params;
    const basename = `/course/detail/${courseNo}/${courseSeq}/${weekTimeId}`;

    const courseList = useAtomValue(
        CourseListAtom({
            courseNo: courseNo,
            courseSeq: courseSeq
        })
    );
    const path = history.location.pathname
        .substr(basename.length + 1)
        .split("/")[0]; // 模块路径

    const [course] = useRequest(
        () => getCourseDetail(courseList, Number(weekTimeId)),
        {},
        [weekTimeId]
    );

    // 不考虑无课程的情况
    if (!course) return <CourseSpin />;

    return (
        <CourseContext.Provider
            value={{ course, basename, path }}
            key={course.weekTimeId}>
            <Switch>
                <LayoutRoute
                    exact
                    path={`${basename}/signin`}
                    component={SignIn}
                    layout={CourseLayout}
                />

                <LayoutRoute
                    exact
                    path={`${basename}/rollcall`}
                    component={RollCall}
                    layout={CourseLayout}
                />

                <LayoutRoute
                    exact
                    path={`${basename}/vote`}
                    component={Vote}
                    layout={CourseLayout}
                />

                <LayoutRoute
                    exact
                    path={`${basename}/homework/:courseNo/:courseSeq/:weekTime/:week/:section/:continunity`}
                    component={Homework}
                    layout={CourseLayout}
                />

                <Route
                    exact
                    path={`${basename}/signin/:id/:type`}
                    component={SignInDetail}
                />

                <Route
                    exact
                    path={`${basename}/vote/:id`}
                    component={VoteDetail}
                />

                {isTrue(course.isCloud) && (
                    <LayoutRoute
                        key={course.weekTimeId}
                        path={`${basename}/experiment`}
                        exact
                        component={Experiment}
                        layout={CourseLayout}
                    />
                )}

                <LayoutRoute
                    key={course.weekTimeId}
                    path={`${basename}/exercise`}
                    exact
                    component={Exercise}
                    layout={CourseLayout}
                />

                <Route
                    key={course.weekTimeId}
                    path={`${basename}/exercise/topics/:exerciseId`}
                    exact
                    component={ExerciseTopics}
                />

                <Route
                    key={course.weekTimeId}
                    path={`${basename}/exercise/detail/:exerciseId`}
                    exact
                    component={ExerciseDetail}
                />

                <LayoutRoute
                    exact
                    path={`${basename}/courseware`}
                    component={Courseware}
                    layout={CourseLayout}
                />

                <LayoutRoute
                    exact
                    path={`${basename}/teams`}
                    component={Teams}
                    layout={CourseLayout}
                />

                <LayoutRoute
                    exact
                    path={`${basename}/teams/my`}
                    component={MyTeam}
                    layout={TeamLayout}
                />

                <LayoutRoute
                    exact
                    path={`${basename}/teamDetail/:taskId/:activityId/:groupType/:groupId/:groupLeaderNo`}
                    component={TeamDetail}
                    layout={TeamLayout}
                />

                <Route
                    key={course.weekTimeId}
                    path={`${basename}/report`}
                    exact
                    component={Report}
                />

                <LayoutRoute
                    key={course.weekTimeId}
                    path={`${basename}/meeting`}
                    exact
                    component={Meeting}
                    layout={CourseLayout}
                />

                <Route
                    key={course.weekTimeId}
                    path={`${basename}/meeting/:meetingId`}
                    exact
                    component={MeetingDetail}
                />

                <LayoutRoute
                    key={course.weekTimeId}
                    path={`${basename}/discuss`}
                    exact
                    component={Discuss}
                    layout={CourseLayout}
                />

                <LayoutRoute
                    key={course.weekTimeId}
                    path={`${basename}/discuss/:uuid`}
                    exact
                    component={DiscussDetail}
                    layout={SubLayout}
                />
                <LayoutRoute
                    key={course.weekTimeId}
                    path={`${basename}/question`}
                    exact
                    component={Question}
                    layout={CourseLayout}
                />

                <Route
                    key={course.weekTimeId}
                    path={`${basename}/question/detail/:questionnaireId`}
                    exact
                    component={QuestionDetail}
                />

                <Route
                    key={course.weekTimeId}
                    path={`${basename}/question/topics/:questionnaireId`}
                    exact
                    component={QuestionTopics}
                />

                <Redirect to={`${basename}/courseware`} />
            </Switch>
        </CourseContext.Provider>
    );
}
